import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface CachedData<T> {
  data: T;
  timestamp: number;
}

interface DashboardCacheContextType {
  kvsSamagamData: any | null;
  teacherAttendanceData: any | null;
  studentAttendanceData: any | null;
  setCachedKvsSamagam: (data: any) => void;
  setCachedTeacherAttendance: (data: any) => void;
  setCachedStudentAttendance: (data: any) => void;
  clearDashboardCache: () => void;
}

const DashboardCacheContext = createContext<DashboardCacheContextType | undefined>(undefined);

// Cache TTL: 5 minutes (300000ms) - adjust as needed
const CACHE_TTL = 5 * 60 * 1000;

function getCachedDataIfValid<T>(key: string): T | null {
  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;
    
    const cached: CachedData<T> = JSON.parse(stored);
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - cached.timestamp < CACHE_TTL) {
      // console.log(`✅ Cache HIT for ${key} (age: ${Math.round((now - cached.timestamp) / 1000)}s)`);
      return cached.data;
    } else {
      // console.log(`⏰ Cache EXPIRED for ${key} (age: ${Math.round((now - cached.timestamp) / 1000)}s)`);
      sessionStorage.removeItem(key);
      return null;
    }
  } catch {
    return null;
  }
}

function setCachedDataWithTimestamp<T>(key: string, data: T): void {
  try {
    const cached: CachedData<T> = {
      data,
      timestamp: Date.now()
    };
    sessionStorage.setItem(key, JSON.stringify(cached));
    // console.log(`💾 Cached ${key} with timestamp`);
  } catch (error) {
    // console.warn(`Failed to cache ${key}:`, error);
  }
}

export function DashboardCacheProvider({ children }: { children: React.ReactNode }) {
  const [kvsSamagamData, setKvsSamagamData] = useState<any | null>(() => 
    getCachedDataIfValid('kvsSamagamData')
  );
  const [teacherAttendanceData, setTeacherAttendanceData] = useState<any | null>(() => 
    getCachedDataIfValid('teacherAttendanceData')
  );
  const [studentAttendanceData, setStudentAttendanceData] = useState<any | null>(() => 
    getCachedDataIfValid('studentAttendanceData')
  );

  const setCachedKvsSamagam = useCallback((data: any) => {
    setKvsSamagamData(data);
    setCachedDataWithTimestamp('kvsSamagamData', data);
  }, []);

  const setCachedTeacherAttendance = useCallback((data: any) => {
    setTeacherAttendanceData(data);
    setCachedDataWithTimestamp('teacherAttendanceData', data);
  }, []);

  const setCachedStudentAttendance = useCallback((data: any) => {
    setStudentAttendanceData(data);
    setCachedDataWithTimestamp('studentAttendanceData', data);
  }, []);

  const clearDashboardCache = useCallback(() => {
    setKvsSamagamData(null);
    setTeacherAttendanceData(null);
    setStudentAttendanceData(null);
    try {
      sessionStorage.removeItem('kvsSamagamData');
      sessionStorage.removeItem('teacherAttendanceData');
      sessionStorage.removeItem('studentAttendanceData');
    } catch {
      // Silently fail
    }
  }, []);

  const contextValue = useMemo(() => ({
    kvsSamagamData,
    teacherAttendanceData,
    studentAttendanceData,
    setCachedKvsSamagam,
    setCachedTeacherAttendance,
    setCachedStudentAttendance,
    clearDashboardCache,
  }), [kvsSamagamData, teacherAttendanceData, studentAttendanceData, setCachedKvsSamagam, setCachedTeacherAttendance, setCachedStudentAttendance, clearDashboardCache]);

  return (
    <DashboardCacheContext.Provider value={contextValue}>
      {children}
    </DashboardCacheContext.Provider>
  );
}

export function useDashboardCache() {
  const context = useContext(DashboardCacheContext);
  if (!context) {
    throw new Error('useDashboardCache must be used within DashboardCacheProvider');
  }
  return context;
}
